import { ChevronDown, ClipboardCheck, Home, Layers, List, Play, Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { TextInput } from "@shared/ui/TextInput";

import { usePaneManager } from "./store";
import type { PaneType } from "./types";

type Destination = { type: PaneType; label: string; icon: React.ReactNode };

/** 行き先の5画面。アイコンはホームのカードと同じ形を使い、新しい絵柄は持ち込まない。 */
const DESTINATIONS: Destination[] = [
    { type: "home", label: "ホーム", icon: <Home size={16} /> },
    { type: "generation-setting", label: "生成設定", icon: <List size={16} /> },
    { type: "format", label: "フォーマット登録", icon: <Upload size={16} /> },
    { type: "generate", label: "スライド生成", icon: <Play size={16} /> },
    { type: "results", label: "生成結果", icon: <ClipboardCheck size={16} /> },
];

/**
 * 区切りの下に置く移動の口。行き先ではない。押すとスライドと要素の画面を開いて前に出すだけで、
 * PowerPoint 側の選択は動かさない。その画面は編集中のスライドに追従するため、開けば今の枚が映る。
 */
const CURRENT_SLIDE: Destination = {
    type: "slide",
    label: "編集中のスライドへ",
    icon: <Layers size={16} />,
};

function matches(destination: Destination, query: string): boolean {
    const word = query.trim();
    return word.length === 0 || destination.label.includes(word);
}

/**
 * 題名を引き金にした画面ナビゲーションメニュー（[[2026-08-18 設計ノート アドイン 画面ナビゲーションと履歴]]）。
 * Web コンソールの NavMenu と同じ骨格で、狭いタスクペイン向けに検索欄とアイコンを足している。
 *
 * 題名の幅は固定する。溢れた分は省略記号で切り、正式名称は `title` 属性で読ませる。文字数で切ると
 * 英数字混じりの名前で見た目の幅が変わり、隣の戻る・進むの位置が画面ごとに動くため。
 *
 * スライドと生成担当の画面は行き先の5つに含まれない。そこに居るときは**どの項目にも印を付けない**。
 * 近い項目へ印を付けると、そこに居ると誤解させる。現在地は題名そのものが示している。
 */
export function PaneMenu({ title }: { title: string }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const rootRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const openPane = usePaneManager((s) => s.openPane);
    const activeType = usePaneManager((s) => s.panes.find((p) => p.uid === s.activePaneUid)?.type);

    useEffect(() => {
        // tsconfig の noImplicitReturns に合わせ、片方の道でも値を返す。
        if (!open) return undefined;
        const onDown = (e: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setOpen(false);
        };
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    // 開いた瞬間に検索欄へ入れる。入らないと、狭いペインで欄を押してから打つ二手になる。
    useEffect(() => {
        if (open) menuRef.current?.querySelector("input")?.focus();
    }, [open]);

    function toggle() {
        setQuery("");
        setOpen((v) => !v);
    }

    function go(type: PaneType) {
        setOpen(false);
        openPane(type);
    }

    const destinations = DESTINATIONS.filter((d) => matches(d, query));
    const showCurrentSlide = matches(CURRENT_SLIDE, query);

    return (
        <div ref={rootRef} className="relative">
            <button
                type="button"
                onClick={toggle}
                aria-haspopup="menu"
                aria-expanded={open}
                title={title}
                className="relative z-30 flex shrink-0 items-center gap-0.5 rounded py-0.5 pr-1 text-left transition-colors hover:bg-text-primary/5"
            >
                <span className="w-[6.2em] truncate text-sm font-semibold">{title}</span>
                <ChevronDown size={14} className="shrink-0 text-text-muted" />
            </button>
            {open && (
                <>
                    <div
                        className="fixed inset-0 z-20 bg-black/40"
                        onClick={() => setOpen(false)}
                        aria-hidden
                    />
                    <div
                        ref={menuRef}
                        role="menu"
                        className="absolute left-0 top-9 z-30 w-56 overflow-hidden rounded-lg border border-border-subtle bg-surface py-1 shadow-lg"
                    >
                        <div className="px-2 pb-1.5 pt-1">
                            <TextInput
                                value={query}
                                onChange={setQuery}
                                type="search"
                                size="sm"
                                fullWidth
                                placeholder="画面を探す"
                                ariaLabel="画面を探す"
                            />
                        </div>
                        {destinations.map((d) => (
                            <MenuItem
                                key={d.type}
                                destination={d}
                                current={d.type === activeType}
                                onSelect={go}
                            />
                        ))}
                        {showCurrentSlide && (
                            <>
                                {destinations.length > 0 && (
                                    <div className="my-1 border-t border-border-subtle" />
                                )}
                                <MenuItem
                                    destination={CURRENT_SLIDE}
                                    current={false}
                                    onSelect={go}
                                />
                            </>
                        )}
                        {destinations.length === 0 && !showCurrentSlide && (
                            <p className="px-3 py-2 text-xs text-text-muted">見つかりません</p>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

function MenuItem({
    destination,
    current,
    onSelect,
}: {
    destination: Destination;
    current: boolean;
    onSelect: (type: PaneType) => void;
}) {
    return (
        <button
            type="button"
            role="menuitem"
            aria-current={current ? "true" : undefined}
            onClick={() => onSelect(destination.type)}
            className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-text-primary transition-colors hover:bg-sidebar-bg ${current ? "row-selected font-semibold" : ""}`}
        >
            <span className="shrink-0 text-text-muted">{destination.icon}</span>
            <span className="truncate">{destination.label}</span>
        </button>
    );
}
